import * as React from 'react';
import {
  Checkbox,
  OutlinedInput,
  MenuItem,
  FormControl,
  ListItemText,
  Select,
  type SelectChangeEvent,
  type TextFieldProps,
  InputLabel,
} from '@mui/material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

export type DropDownSelectOption = {
  value: string;
  label: string;
};

type DropdownSelectProps = Omit<TextFieldProps, 'onChange'> & {
  options: DropDownSelectOption[];
  onChange: (selectedValue: string[], index?: number) => void;
  value: string[];
  index?: number;
  displayLabel?: boolean;
  maxWidth?: string;
};

function replaceValuesWithLabels(
  initialVal: string[],
  options: DropDownSelectOption[],
): string[] {
  return initialVal.map(value => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value;
  });
}

export const DropdownSelect: React.FunctionComponent<DropdownSelectProps> = (
  props: DropdownSelectProps,
) => {
  const {
    options,
    onChange,
    index,
    disabled,
    displayLabel = true,
    value = [],
    maxWidth = '340px',
    label,
  } = props;

  const [checkedOptionValues, setCheckedOptionValues] =
    React.useState<string[]>(value);

  const handleChange = (
    event: SelectChangeEvent<typeof checkedOptionValues>,
  ): void => {
    const {
      target: { value },
    } = event;

    setCheckedOptionValues(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );

    const selectedValue = value as string[];
    onChange(selectedValue, index);
  };

  React.useEffect(() => {
    // update states when props update
    // change to controlled component
    setCheckedOptionValues(value);
  }, [value]);

  return (
    <div className="w-full">
      <FormControl sx={{ m: 1, width: '90%', maxWidth }} disabled={disabled}>
        <InputLabel
          id="demo-multiple-checkbox-label"
          size="small"
          shrink
          disabled={disabled}
          sx={{
            background: 'white',
            px: 0.5,
            zIndex: 1,
          }}
        >
          {label}
        </InputLabel>
        <Select
          labelId="demo-multiple-checkbox-label"
          id="demo-multiple-checkbox"
          size="small"
          multiple
          displayEmpty
          value={checkedOptionValues}
          onChange={handleChange}
          input={<OutlinedInput />}
          renderValue={selected => {
            if (selected?.length === 0) {
              return <em>[Default Select All]</em>;
            }
            // to avoid display enums directly
            return (
              displayLabel
                ? replaceValuesWithLabels(selected, options)
                : selected
            ).join(',');
          }}
          inputProps={{ 'aria-label': 'Without label' }}
          MenuProps={MenuProps}
        >
          <MenuItem disabled value="">
            <em>[Default Select All]</em>
          </MenuItem>
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox
                size="small"
                checked={(checkedOptionValues ?? [])?.includes(option.value)}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
